import { RefObject, useEffect } from 'react';

export type UseInfiniteScrollParams = {
  /** Assigned to the last row. Its coming into view is what asks for more. */
  lastRowRef: RefObject<Element>;
  /** The scrolling box the row is watched against, not the viewport. */
  containerRef: RefObject<Element>;
  fetchMore: () => void;
  /** Nothing further to ask for, so no observer is set up at all. */
  hasMore: boolean;
  /**
   * How many rows are loaded. The observer watches one element, so it has to be
   * set up again on the new last row once a page lands — this is what tells it
   * to.
   */
  rowCount: number;
  /**
   * Off while the list is not showing its rows. A collapsed group has no last
   * row to watch, and would otherwise page as soon as it opened.
   */
  enabled?: boolean;
};

/**
 * Asks for the next page when the last row scrolls into view.
 *
 * Paging itself is the caller's — this only says when. The two lists that use
 * it page differently: the suggestion queue merges pages in `useSuggestions`,
 * the articles table in `useArticleInfiniteScroll`.
 */
export function useInfiniteScroll({
  lastRowRef,
  containerRef,
  fetchMore,
  hasMore,
  rowCount,
  enabled = true,
}: UseInfiniteScrollParams): void {
  useEffect(() => {
    if (!enabled || !hasMore || !lastRowRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchMore();
      },
      { root: containerRef.current },
    );

    observer.observe(lastRowRef.current);
    return () => observer.disconnect();
    // `rowCount` is not read in here: it is the signal that the last row has
    // changed, since a ref holding it cannot say so on its own.
  }, [enabled, hasMore, fetchMore, containerRef, lastRowRef, rowCount]);
}
